import type { ReactNode } from 'react'

import type {
  DataTableAction,
  DataTableBulkAction,
  DataTableColumn,
  DataTableFilter,
  SortOrder,
} from './data-table.interface'
import { useDataTableSelection } from './useDataTableSelection.hook'
import { useDataTableSort } from './useDataTableSort.hook'

interface UseDataTableProps<TData> {
  data: TData[]
  columnList: DataTableColumn<TData>[]
  getRowId: (row: TData) => string
  isSelectable: boolean
  selectedIdList: string[]
  onSelectionChange?: (selectedIdList: string[]) => void
  actionList: DataTableAction<TData>[]
  bulkActionList?: DataTableBulkAction[]
  sortBy?: string
  sortOrder?: SortOrder
  onSortChange?: (sortBy: string, sortOrder: SortOrder) => void
  onSearchChange?: (value: string) => void
  filterList?: DataTableFilter[]
  toolbarActions?: ReactNode
}

interface UseDataTableReturn {
  hasToolbar: boolean
  hasActions: boolean
  totalColSpan: number
  isAllSelected: boolean
  isIndeterminate: boolean
  handleSelectAll: (isChecked: boolean) => void
  handleSelectRow: (rowId: string, isChecked: boolean) => void
  isRowSelected: (rowId: string) => boolean
  handleSort: (columnId: string) => void
  getColumnSortOrder: (columnId: string) => SortOrder | null
  handleBulkAction: (actionId: string, selectedIdList: string[]) => void
}

/**
 * Hook that processes data table state and provides handlers.
 */
export const useDataTable = <TData>({
  data,
  columnList,
  getRowId,
  isSelectable,
  selectedIdList,
  onSelectionChange,
  actionList,
  bulkActionList,
  sortBy,
  sortOrder,
  onSortChange,
  onSearchChange,
  filterList,
  toolbarActions,
}: UseDataTableProps<TData>): UseDataTableReturn => {
  const hasToolbar =
    !!onSearchChange ||
    (filterList !== undefined && filterList.length > 0) ||
    toolbarActions !== undefined ||
    (bulkActionList !== undefined && bulkActionList.length > 0)

  const hasActions = actionList.length > 0

  const { isAllSelected, isIndeterminate, handleSelectAll, handleSelectRow, isRowSelected } =
    useDataTableSelection({ data, getRowId, selectedIdList, onSelectionChange })

  const { handleSort, getColumnSortOrder } = useDataTableSort({ sortBy, sortOrder, onSortChange })

  const handleBulkAction = (actionId: string, idList: string[]): void => {
    const action = bulkActionList?.find((a) => a.id === actionId)
    action?.onClick(idList)
  }

  const totalColSpan = columnList.length + (isSelectable ? 1 : 0) + (hasActions ? 1 : 0)

  return {
    hasToolbar,
    hasActions,
    totalColSpan,
    isAllSelected,
    isIndeterminate,
    handleSelectAll,
    handleSelectRow,
    isRowSelected,
    handleSort,
    getColumnSortOrder,
    handleBulkAction,
  }
}
